/**
 * Der Datei-Slot des Fragebogens (`type: "documents"`).
 *
 * Was hier steht, ist gespeicherter Serverzustand: Der Antwortwert ist eine
 * Liste vom SERVER erzeugter {@link DocumentRef}s, nie ein Dateiinhalt.
 * Dateiinhalte gehören nie in die Answers — `saveDraft` verschluckt einen
 * Quota-Fehler des localStorage still (`draftStorage.ts`), während die Leiste
 * „gespeichert" meldet; der Nutzer verlöre damit ALLE Antworten, nicht nur die
 * Datei. Hochgeladen wird deshalb sofort gegen die API, und erst die Antwort
 * des Servers landet im Antwortsatz.
 *
 * **Schreiben kann nur der Beratermodus** (`uploadTarget` = `internal`, AP4).
 * `readonly` ist die Vorgabe jeder Bestandsansicht (Auswertung, Prüf-Modal);
 * `token` (öffentliches Kundenformular) bleibt bis AP7 bewusst reine Anzeige —
 * dort fehlen der Nachreich-Lesepfad und der eigene Fehlerdialog des
 * Kundenkanals.
 *
 * **Grenzen und Meldungen kommen vom Server.** Vorab prüft dieses Feld nur,
 * was sich ohne Anfrage entscheiden lässt und wofür es eine Zahl aus dem
 * FieldDef hat (`maxFiles`, `maxSizeMb` — beide aus der Slot-Tabelle des
 * Backends gespiegelt). Den Dateityp prüft bewusst NUR der Server: er
 * entscheidet über Endung UND Magic Bytes, und seine Meldung ist die einzige,
 * die stimmt. Ein zweiter Typ-Filter hier verböte Dateien, die der Server
 * annähme.
 */
import { makeStyles, shorthands } from "@fluentui/react-components";
import {
  ArrowUpload20Regular,
  Delete20Regular,
  Document24Regular,
} from "@fluentui/react-icons";
import * as React from "react";
import {
  documentRefAnzeige,
  documentRefs,
  DOKUMENTE_NUR_ANSICHT_HINWEIS,
} from "@kmvs/forms-core";
import type { DocumentRef, FieldDef } from "@kmvs/forms-core";
import {
  entferneAnalyseUpload,
  ladeAnalyseUploadHoch,
} from "../../services/risikoanalyseUploadService";
import { BRAND, REFINED } from "./tokens";
import { kannHochladen, useUploadTarget } from "./uploadTarget";

const useStyles = makeStyles({
  wrap: {
    display: "block",
  },
  hinweis: {
    display: "block",
    marginBottom: "8px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    lineHeight: 1.35,
    color: BRAND.muted,
  },
  liste: {
    display: "grid",
    rowGap: "8px",
  },
  zeile: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "10px",
    backgroundColor: BRAND.white,
    ...shorthands.padding("9px", "12px"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", BRAND.border),
  },
  symbol: {
    color: REFINED.aub,
    flexShrink: 0,
    marginTop: "1px",
  },
  text: {
    display: "grid",
    rowGap: "2px",
    minWidth: 0,
    flexGrow: 1,
  },
  name: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    wordBreak: "break-word",
  },
  meta: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.76rem",
    color: BRAND.muted,
  },
  notiz: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.8rem",
    color: BRAND.ink,
    fontStyle: "italic",
  },
  chip: {
    display: "inline-block",
    marginLeft: "8px",
    fontSize: "0.66rem",
    fontWeight: 600,
    letterSpacing: "0.03em",
    color: REFINED.goldInk,
    backgroundColor: REFINED.goldBg,
    ...shorthands.padding("1px", "8px"),
    ...shorthands.borderRadius("999px"),
  },
  leer: {
    display: "block",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.86rem",
    color: BRAND.muted,
    fontStyle: "italic",
    backgroundColor: REFINED.fieldBg,
    ...shorthands.padding("10px", "12px"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "dashed", BRAND.borderDashed),
  },
  entfernen: {
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    backgroundColor: "transparent",
    color: BRAND.muted,
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.76rem",
    ...shorthands.padding("2px", "6px"),
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", BRAND.border),
    ":hover": {
      color: BRAND.ink,
    },
    ":disabled": {
      cursor: "default",
      opacity: 0.5,
    },
  },
  aktionen: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    marginTop: "10px",
    flexWrap: "wrap",
  },
  waehlen: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.86rem",
    ...shorthands.padding("8px", "14px"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", REFINED.aub),
    ":disabled": {
      cursor: "default",
      opacity: 0.5,
    },
  },
  laufend: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.8rem",
    color: BRAND.muted,
  },
  fehler: {
    display: "block",
    marginTop: "8px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    lineHeight: 1.35,
    color: "#a4262c",
    backgroundColor: "rgba(164,38,44,0.06)",
    ...shorthands.padding("8px", "10px"),
    ...shorthands.borderRadius("8px"),
  },
  fussnote: {
    display: "block",
    marginTop: "8px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.74rem",
    lineHeight: 1.35,
    color: BRAND.muted,
  },
  versteckt: {
    display: "none",
  },
});

/** „PDF, JPG · höchstens 5 Dateien · bis 10 MB je Datei" — nur, was gesetzt ist. */
export const slotHinweis = (field: FieldDef): string => {
  const teile: string[] = [];
  if (field.accept?.length) {
    teile.push(
      field.accept
        .map((eintrag) => eintrag.replace(/^\./, "").split("/").pop() ?? eintrag)
        .map((eintrag) => eintrag.toUpperCase())
        .filter((eintrag, index, alle) => alle.indexOf(eintrag) === index)
        .join(", ")
    );
  }
  if (field.maxFiles) teile.push(`höchstens ${field.maxFiles} Dateien`);
  if (field.maxSizeMb) teile.push(`bis ${field.maxSizeMb} MB je Datei`);
  return teile.join(" · ");
};

/**
 * Was „Entfernen" wirklich tut — wörtlich am Feld, nicht nur im Plan.
 * Der Server setzt `verworfen`, es gibt kein Hard-Delete: ein Fehlklick kostet
 * die Datei nicht.
 */
export const ENTFERNEN_HINWEIS =
  "„Entfernen“ nimmt die Datei nur aus dem Fragebogen — in der Kundenakte bleibt sie erhalten.";

type Props = {
  field: FieldDef;
  value: unknown;
  /**
   * Schreibt die neue Dateiliste in den Antwortsatz. Fehlt sie (Bestands-
   * ansichten, die `QuestionField` ohne Änderungsabsicht rendern), bleibt der
   * Slot reine Anzeige — auch mit gesetztem Upload-Ziel.
   */
  onChange?: (dateien: DocumentRef[]) => void;
};

const DocumentUploadField = ({ field, value, onChange }: Props) => {
  const styles = useStyles();
  const ziel = useUploadTarget();
  const dateien = documentRefs(value);

  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [laufendeDatei, setLaufendeDatei] = React.useState<string | null>(null);
  const [entferntId, setEntferntId] = React.useState<number | null>(null);
  const [fehler, setFehler] = React.useState<string | null>(null);

  /**
   * Ein Upload überlebt den Schritt- oder Seitenwechsel — dieses Feld nicht.
   *
   * `montiertRef` deckt deshalb **nur den eigenen Zustand** ab (Fehlerzeile,
   * „wird hochgeladen…"): Den nach dem Ausbauen zu setzen wäre wirkungslos.
   *
   * **Der Rückkanal `onChange` hängt ausdrücklich NICHT daran.** Er gehört dem
   * Elternteil, das den Antwortsatz hält (`RisikoanalyseStepForm`), und das
   * bleibt beim Klick auf „Weiter" montiert — nur der Slot verschwindet. Wurde
   * `onChange` hier verschluckt, lag die Datei in Datenbank und Objektspeicher,
   * fehlte aber im Antwortsatz: keine Zeile, kein „Entfernen", und das
   * 150-MB-Budget des Kontakts dauerhaft belegt. React 18 warnt bei setState
   * nach dem Ausbauen nicht mehr — es gibt keinen Grund mehr, das Ergebnis
   * wegzuwerfen.
   */
  const montiertRef = React.useRef(true);
  React.useEffect(() => {
    montiertRef.current = true;
    return () => {
      montiertRef.current = false;
    };
  }, []);

  /** Eigener Zustand nur, solange das Feld noch am Bildschirm hängt. */
  const wennMontiert = (setzen: () => void) => {
    if (montiertRef.current) setzen();
  };

  /**
   * Nur der Beratermodus schreibt. `kannHochladen` beantwortet die Frage des
   * Contexts („ist überhaupt ein Ziel gesetzt?"), sagt aber nichts darüber, ob
   * dieses Feld den Kanal auch bedienen kann: `token` wartet auf AP7.
   */
  const zielKontakt = ziel.mode === "internal" ? ziel.contactId : null;
  const schreibbar =
    kannHochladen(ziel) && zielKontakt != null && typeof onChange === "function";

  const beschaeftigt = laufendeDatei != null || entferntId != null;
  const slotVoll = field.maxFiles != null && dateien.length >= field.maxFiles;

  /**
   * Wie der Slot in einer Meldung heißt.
   *
   * `anzeigeBegriff` zuerst: Die vier Anlagen-Nachweise tragen alle dasselbe
   * `label` („Unterlagen (Attest / Wartungsprotokolle)"), der Server nennt sie
   * aber einzeln („EMA — Attest / Wartungsprotokolle"). Ohne den Begriff nennte
   * dieselbe Grenze hier und dort zwei verschiedene Namen.
   */
  const slotName = field.anzeigeBegriff ?? field.label ?? field.key;

  /**
   * Auflagen nur da, wo sie erfüllbar sind.
   *
   * Der Hinweis „PDF, JPG · höchstens 5 Dateien · bis 10 MB je Datei" stand
   * bis AP4 UNBEDINGT — auch dort, wo es gar keinen Upload-Knopf gibt. Der
   * Leser las Auflagen, hatte keinen Weg, sie zu erfüllen, und keinen Satz,
   * der ihm einen nennt. Solange nichts hochgeladen werden kann, sagt der Slot
   * genau das — mit demselben Satz wie das Portal.
   */
  const hinweis = schreibbar ? slotHinweis(field) : DOKUMENTE_NUR_ANSICHT_HINWEIS;

  /**
   * Lädt die Auswahl der Reihe nach hoch und schreibt nach JEDER Datei zurück:
   * Bricht die dritte von fünf ab, sind die ersten beiden trotzdem im
   * Antwortsatz — und in der Datenbank liegen sie ohnehin.
   *
   * Beim ersten Fehler endet der Durchlauf. Der häufigste Grund ist eine
   * Grenze (Slot voll, Kontingent erschöpft), und die gilt für jede weitere
   * Datei genauso; ein Weiterlaufen produzierte nur dieselbe Meldung fünfmal.
   */
  const verarbeite = async (auswahl: File[]) => {
    if (zielKontakt == null || !onChange) return;
    wennMontiert(() => setFehler(null));
    let liste = dateien;

    for (const datei of auswahl) {
      if (field.maxFiles != null && liste.length >= field.maxFiles) {
        wennMontiert(() =>
          setFehler(
            `Für „${slotName}“ sind höchstens ${field.maxFiles} Dateien möglich. Bitte zuerst eine Datei entfernen.`
          )
        );
        break;
      }
      if (field.maxSizeMb != null && datei.size > field.maxSizeMb * 1024 * 1024) {
        wennMontiert(() =>
          setFehler(`„${datei.name}“ ist zu groß — höchstens ${field.maxSizeMb} MB je Datei.`)
        );
        break;
      }

      wennMontiert(() => setLaufendeDatei(datei.name));
      try {
        const ref = await ladeAnalyseUploadHoch({
          contactId: zielKontakt,
          fieldKey: field.key,
          datei,
        });
        liste = [...liste, ref];
        // Bewusst ohne Montage-Prüfung — siehe `montiertRef` oben.
        onChange(liste);
      } catch (err) {
        wennMontiert(() => setFehler(err instanceof Error ? err.message : String(err)));
        break;
      } finally {
        wennMontiert(() => setLaufendeDatei(null));
      }
    }
  };

  const handleAuswahl = (event: React.ChangeEvent<HTMLInputElement>) => {
    const auswahl = Array.from(event.target.files ?? []);
    // Zurücksetzen, damit dieselbe Datei nach einem Fehler erneut wählbar ist.
    event.target.value = "";
    if (auswahl.length === 0) return;
    void verarbeite(auswahl);
  };

  const entferne = async (ref: DocumentRef) => {
    if (zielKontakt == null || !onChange) return;
    wennMontiert(() => setFehler(null));
    wennMontiert(() => setEntferntId(ref.documentId));
    try {
      await entferneAnalyseUpload(zielKontakt, ref.documentId);
      // Auch hier ohne Montage-Prüfung: der Server hat die Datei bereits
      // abgehängt — bliebe sie im Antwortsatz stehen, zeigte der Fragebogen
      // eine Zeile mit einem Download-Ziel, das es nicht mehr gibt.
      onChange(dateien.filter((eintrag) => eintrag.documentId !== ref.documentId));
    } catch (err) {
      wennMontiert(() => setFehler(err instanceof Error ? err.message : String(err)));
    } finally {
      wennMontiert(() => setEntferntId(null));
    }
  };

  return (
    <div
      className={styles.wrap}
      /*
       * Das Ziel steht am Element, nicht nur im Kopf des Entwicklers: So ist
       * im Test wie im Browser nachprüfbar, dass ohne Provider tatsächlich
       * „readonly" ankommt — die Vorgabe, die jede Bestandsansicht schützt.
       */
      data-upload-modus={ziel.mode}
      data-upload-schreibbar={schreibbar ? "ja" : "nein"}
    >
      {hinweis && <span className={styles.hinweis}>{hinweis}</span>}
      {dateien.length === 0 ? (
        <span className={styles.leer}>Noch keine Unterlagen hinterlegt.</span>
      ) : (
        <div className={styles.liste}>
          {dateien.map((ref) => {
            // Dieselbe Herleitung wie im Portal (forms-core): Was eine
            // Dateizeile zeigt, entscheidet EINE Stelle — sonst fehlt der
            // einen Seite wieder, was die andere längst anzeigt.
            const anzeige = documentRefAnzeige(ref);
            return (
              <div key={ref.documentId} className={styles.zeile}>
                <Document24Regular className={styles.symbol} />
                <span className={styles.text}>
                  <span className={styles.name}>
                    {anzeige.name}
                    {anzeige.herkunft && (
                      <span className={styles.chip}>{anzeige.herkunft}</span>
                    )}
                  </span>
                  {anzeige.meta && <span className={styles.meta}>{anzeige.meta}</span>}
                  {anzeige.notiz && <span className={styles.notiz}>{anzeige.notiz}</span>}
                </span>
                {schreibbar && (
                  <button
                    type="button"
                    className={styles.entfernen}
                    onClick={() => void entferne(ref)}
                    disabled={beschaeftigt}
                    title={ENTFERNEN_HINWEIS}
                  >
                    <Delete20Regular />
                    {entferntId === ref.documentId ? "wird entfernt…" : "Entfernen"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {schreibbar && (
        <>
          <div className={styles.aktionen}>
            <input
              ref={inputRef}
              type="file"
              className={styles.versteckt}
              multiple
              accept={field.accept?.join(",")}
              onChange={handleAuswahl}
              // Der Slot bleibt sichtbar, wenn er voll ist — nur das Auswählen
              // ist gesperrt, damit klar bleibt, WAS drin liegt.
              disabled={beschaeftigt || slotVoll}
              data-testid={`upload-input-${field.key}`}
            />
            <button
              type="button"
              className={styles.waehlen}
              onClick={() => inputRef.current?.click()}
              disabled={beschaeftigt || slotVoll}
            >
              <ArrowUpload20Regular />
              {dateien.length > 0 ? "Weitere Datei hochladen" : "Datei hochladen"}
            </button>
            {laufendeDatei && (
              <span className={styles.laufend}>{`„${laufendeDatei}“ wird hochgeladen…`}</span>
            )}
            {!laufendeDatei && slotVoll && (
              <span className={styles.laufend}>
                {`Dieser Slot ist voll (${field.maxFiles} Dateien).`}
              </span>
            )}
          </div>
          {fehler && (
            <span className={styles.fehler} role="alert">
              {fehler}
            </span>
          )}
          <span className={styles.fussnote}>{ENTFERNEN_HINWEIS}</span>
        </>
      )}
    </div>
  );
};

export default DocumentUploadField;
